import { formatCents, situationDe, type Situation } from './catalogDerive.js'
import type { Amounts, Service } from './catalog.js'
import type { Game, Pass, Promotion, PromotionAppliesTo, Rounding, Season } from './offers.js'

// Règles de calcul de la page « Offres & Abonnements ». Fonctions pures, sans
// dépendance, dans `contracts` pour la même raison que `catalogDerive` : le
// site, l'administration et l'API doivent produire exactement les mêmes
// chiffres et les mêmes phrases. Les dupliquer rouvrirait la divergence que ce
// chantier ferme.
//
// C'est ici que vivent les libellés aujourd'hui écrits à la main dans le JSX
// (« soit 8,33 € / mois », « 20% moins cher qu'avec deux pass 6 mois ») et qui
// devenaient faux au premier changement de tarif.

// ───────────────────────────── Dates et périodes ─────────────────────────────

const MOIS = [
  'janvier', 'février', 'mars', 'avril', 'mai', 'juin',
  'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre',
]

/**
 * Jour courant au format ISO civil, dans le fuseau de la machine. Les périodes
 * sont des jours de calendrier : c'est bien la date locale qui décide qu'une
 * promotion « jusqu'au 31 août » est encore en ligne le 31 août au soir à
 * Auxerre, quoi qu'en dise UTC.
 */
export function todayIso(now: Date = new Date()): string {
  const mois = String(now.getMonth() + 1).padStart(2, '0')
  const jour = String(now.getDate()).padStart(2, '0')
  return `${now.getFullYear()}-${mois}-${jour}`
}

/** « 1ᵉʳ » pour le premier du mois, le nombre nu sinon — usage français. */
function jourFr(jour: number): string {
  return jour === 1 ? '1ᵉʳ' : String(jour)
}

/** « 2026-08-31 » → « 31 août » ; avec `withYear`, « 31 août 2026 ». */
export function formatFrenchDate(iso: string, withYear = false): string {
  const [annee, mois, jour] = iso.split('-').map(Number)
  return `${jourFr(jour)} ${MOIS[mois - 1]}${withYear ? ` ${annee}` : ''}`
}

/**
 * « 1ᵉʳ juillet – 31 août 2026 ». L'année n'est répétée sur la première date
 * que si la période chevauche deux années — sinon elle alourdit sans informer.
 */
export function formatFrenchPeriod(start: string, end: string): string {
  const memeAnnee = start.slice(0, 4) === end.slice(0, 4)
  return `${formatFrenchDate(start, !memeAnnee)} – ${formatFrenchDate(end, true)}`
}

/** « du 1ᵉʳ au 30 septembre » quand le mois est commun, « du 1ᵉʳ juillet au
 * 31 août » sinon. Forme courte des résumés de liste. */
export function formatShortPeriod(start: string, end: string): string {
  const [, moisDebut, jourDebut] = start.split('-').map(Number)
  const [, moisFin, jourFin] = end.split('-').map(Number)
  if (start.slice(0, 4) === end.slice(0, 4) && moisDebut === moisFin) {
    return `du ${jourFr(jourDebut)} au ${jourFr(jourFin)} ${MOIS[moisFin - 1]}`
  }
  return `du ${formatFrenchDate(start)} au ${formatFrenchDate(end)}`
}

// ──────────────────────── État d'un bloc saisonnier ────────────────────────

export type SeasonState = 'online' | 'scheduled' | 'ended' | 'disarmed'

/**
 * L'état d'un bloc saisonnier au jour dit. Jamais stocké : recalculé à chaque
 * rendu, c'est ce qui fait apparaître puis disparaître un bloc sans que
 * personne n'intervienne.
 *
 * Les dates sont comparées comme du **texte**, pas comme des instants. Deux
 * chaînes « AAAA-MM-JJ » se rangent dans l'ordre chronologique par simple
 * comparaison lexicographique : ni `Date`, ni fuseau, ni heure limite à
 * choisir, donc aucune des erreurs d'un jour que produit un `new Date(iso)`
 * interprété en UTC face à une date saisie en heure locale.
 *
 * Les deux bornes sont **incluses** : armé du 1ᵉʳ juillet au 31 août, le bloc
 * est en ligne ces deux jours-là.
 */
export function seasonState(season: Season, today: string = todayIso()): SeasonState {
  if (!season.armed) return 'disarmed'
  if (today < season.start) return 'scheduled'
  if (today > season.end) return 'ended'
  return 'online'
}

/** Vrai si le bloc doit s'afficher sur le site aujourd'hui. La seule question
 * que le site pose ; les trois autres états ne l'intéressent pas. */
export function isLive(season: Season, today: string = todayIso()): boolean {
  return seasonState(season, today) === 'online'
}

/**
 * Étiquette d'état, accordée en genre (`feminine` pour une promotion). Le
 * français n'a pas d'accord neutre : « Programmé » sur une promotion se lit
 * comme une faute, et corriger ça côté appelant ferait recopier les quatre
 * libellés à chaque écran.
 */
export function seasonStateLabel(state: SeasonState, feminine = false): string {
  const e = feminine ? 'e' : ''
  switch (state) {
    case 'online':
      return 'En ligne'
    case 'scheduled':
      return `Programmé${e}`
    case 'ended':
      return `Terminé${e}`
    case 'disarmed':
      return `Désarmé${e}`
  }
}

/** Résumé court d'une ligne de liste : « En ligne jusqu'au 31 août ». */
export function seasonMeta(season: Season, feminine = false, today: string = todayIso()): string {
  const state = seasonState(season, today)
  const e = feminine ? 'e' : ''
  switch (state) {
    case 'online':
      return `En ligne jusqu’au ${formatFrenchDate(season.end)}`
    case 'scheduled':
      return `Programmé${e} ${formatShortPeriod(season.start, season.end)}`
    case 'ended':
      return `Terminé${e} le ${formatFrenchDate(season.end)}`
    case 'disarmed':
      return `Désarmé${e}`
  }
}

/**
 * Phrase complète de l'éditeur : elle dit ce qui va se passer, et quand. C'est
 * elle qui apprend à la gérante que l'affichage est automatique — sans elle,
 * armer une promotion de septembre en août ressemble à une erreur.
 */
export function seasonPhrase(season: Season, feminine = false, today: string = todayIso()): string {
  const state = seasonState(season, today)
  const e = feminine ? 'e' : ''
  switch (state) {
    case 'online':
      return `En ligne aujourd’hui. Disparaîtra du site le ${formatFrenchDate(season.end, true)} au soir, sans intervention.`
    case 'scheduled':
      // « d'elle-même » s'élide, « de lui-même » non : la maquette produisait
      // « d'lui-même » pour les jeux et le bandeau. Deux formes complètes
      // plutôt qu'une élision conditionnelle.
      return `Armé${e} pour plus tard : apparaîtra ${feminine ? 'd’elle-même' : 'de lui-même'} le ${formatFrenchDate(season.start, true)}, sans intervention.`
    case 'ended':
      return `Période passée : retiré${e} du site depuis le ${formatFrenchDate(season.end, true)}. La saisie est conservée.`
    case 'disarmed':
      return `Désarmé${e} : jamais affiché${e} sur le site, quelle que soit la période. La saisie est conservée.`
  }
}

// ──────────────────────────────── Pass ────────────────────────────────

/** « 80 € / an » pour douze mois, « 50 € / 6 mois » sinon — la formulation de
 * la page publique avant ce lot. */
export function passPriceUnit(pass: Pick<Pass, 'durationMonths'>): string {
  return pass.durationMonths === 12 ? '/ an' : `/ ${pass.durationMonths} mois`
}

/**
 * « soit 6,67 € / mois ». Écrite à la main jusqu'ici, donc fausse dès qu'un
 * prix bougeait sans qu'on y pense. L'arrondi au centime le plus proche est
 * celui de l'affichage d'origine (« soit 8,33 € / mois » pour 50 € sur 6 mois).
 */
export function monthlyEquivalent(pass: Pick<Pass, 'priceCents' | 'durationMonths'>): string {
  const parMois = Math.round(pass.priceCents / pass.durationMonths)
  return `soit ${formatCents(parMois)} / mois`
}

const NOMBRES_EN_MOTS = ['', 'un', 'deux', 'trois', 'quatre', 'cinq', 'six']

/**
 * « 20 % moins cher qu'avec deux pass 6 mois », ou `null` quand la comparaison
 * n'a pas lieu d'être.
 *
 * Le pass de référence est le plus court parmi les pass visibles, et la
 * comparaison n'est produite que si la durée comparée en est un multiple exact
 * — « 20 % moins cher qu'avec 1,83 pass 6 mois » ne veut rien dire. Une
 * économie nulle ou négative n'est pas annoncée non plus : la carte publique
 * n'affiche que ce qui plaide en sa faveur, et un « 0 % moins cher » ferait
 * douter du reste.
 */
export function passComparison(pass: Pass, allPasses: Pass[]): string | null {
  const reference = allPasses
    .filter((autre) => autre.visible && autre.id !== pass.id)
    .reduce<Pass | null>(
      (court, autre) => (court === null || autre.durationMonths < court.durationMonths ? autre : court),
      null,
    )
  if (!reference) return null

  const multiple = pass.durationMonths / reference.durationMonths
  if (!Number.isInteger(multiple) || multiple < 2 || multiple > 6) return null

  const coutEquivalent = reference.priceCents * multiple
  if (coutEquivalent <= 0) return null

  const economie = Math.round((1 - pass.priceCents / coutEquivalent) * 100)
  if (economie <= 0) return null

  return `${economie}% moins cher qu’avec ${NOMBRES_EN_MOTS[multiple]} ${reference.title.toLowerCase()}`
}

/** Le pass que la page publique montre en premier : celui qui est
 * présélectionné, à défaut le premier visible. */
export function preselectedPass(passes: Pass[]): Pass | null {
  const visibles = passes.filter((pass) => pass.visible)
  return visibles.find((pass) => pass.preselected) ?? visibles[0] ?? null
}

// ────────────────────────────── Promotions ──────────────────────────────

/**
 * Applique la remise à un montant, arrondi selon le réglage de la promotion.
 * Sur des centimes entiers, donc sans dérive de virgule flottante : « à
 * l'euro » arrondit à la centaine de centimes la plus proche, « au centime »
 * au centime le plus proche.
 */
export function discountedCents(cents: number, discountPercent: number, rounding: Rounding): number {
  const remise = (cents * (100 - discountPercent)) / 100
  return rounding === 'euro' ? Math.round(remise / 100) * 100 : Math.round(remise)
}

/**
 * Applique la remise aux montants d'un tarif, en conservant sa forme. Un
 * montant « non proposé » (`null`) le reste : une remise ne crée pas un tarif
 * qui n'existe pas.
 *
 * `appliesTo` choisit les montants touchés (voir `promotionAppliesToSchema`).
 * Avec `forfait`, seul le prix du forfait baisse. Un tarif sans forfait reste
 * alors inchangé, sans retomber sur une remise de tout le tarif : la gérante a
 * exclu la séance à l'unité, et une prestation dont le tarif a changé depuis
 * sa sélection ne doit pas rouvrir cette remise en silence.
 */
export function discountedAmounts(
  amounts: Amounts,
  discountPercent: number,
  rounding: Rounding,
  appliesTo: PromotionAppliesTo = 'tout',
): Amounts {
  const remise = (cents: number) => discountedCents(cents, discountPercent, rounding)
  const remiseOuNull = (cents: number | null) => (cents === null ? null : remise(cents))

  if (appliesTo === 'forfait') {
    // Le forfait baisse, la séance ne bouge pas : prixForfait ne peut que
    // s'éloigner de prixSeance × nombreSeances, l'invariant du schéma tient.
    return amounts.type === 'forfait' ? { ...amounts, prixForfait: remise(amounts.prixForfait) } : amounts
  }

  switch (amounts.type) {
    case 'pass':
      return { ...amounts, avecPass: remiseOuNull(amounts.avecPass), sansPass: remiseOuNull(amounts.sansPass) }
    case 'duo':
      return {
        ...amounts,
        seuleAvecPass: remiseOuNull(amounts.seuleAvecPass),
        seuleSansPass: remiseOuNull(amounts.seuleSansPass),
        aDeux: remiseOuNull(amounts.aDeux),
      }
    case 'quantite':
      return { ...amounts, total: remise(amounts.total) }
    case 'forfait':
      // Les deux montants suivent la même remise : le forfait reste
      // proportionnellement remisé, donc toujours strictement inférieur au
      // produit prixSeance × nombreSeances — l'invariant qu'`amountsSchema`
      // exige d'un « forfait » n'est pas cassé par la remise.
      return { ...amounts, prixSeance: remise(amounts.prixSeance), prixForfait: remise(amounts.prixForfait) }
    case 'unique':
      return { ...amounts, prix: remise(amounts.prix) }
  }
}

/**
 * Économie réalisée sur le montant de référence d'un tarif, en centimes. Elle
 * se lit sur le tarif remisé lui-même, pour ne jamais annoncer une économie
 * sur un montant que `appliesTo` laisse intact.
 */
export function savingCents(
  amounts: Amounts,
  discountPercent: number,
  rounding: Rounding,
  appliesTo: PromotionAppliesTo = 'tout',
): number | null {
  const reference = referenceCents(amounts)
  if (reference === null) return null
  return reference - referenceCents(discountedAmounts(amounts, discountPercent, rounding, appliesTo))!
}

/**
 * Montant sur lequel l'économie est annoncée : le tarif adhérent quand il
 * existe — c'est la colonne « Avec pass » que le tableau de la page met en
 * avant — sinon le seul montant disponible.
 */
function referenceCents(amounts: Amounts): number | null {
  switch (amounts.type) {
    case 'pass':
      return amounts.avecPass ?? amounts.sansPass
    case 'duo':
      return amounts.seuleAvecPass ?? amounts.seuleSansPass
    case 'quantite':
      return amounts.total
    case 'forfait':
      return amounts.prixForfait
    case 'unique':
      return amounts.prix
  }
}

/** Ligne du tableau d'une promotion : prix d'origine, prix remisé, économie —
 * tous calculés depuis le catalogue, aucun saisi. `group` est le nom du groupe
 * du catalogue (« Lumière pulsée — Dépilation Femmes ») : sans lui, deux
 * « Aisselles » d'une même promotion ne se distinguent pas. `null` quand
 * l'appelant ne le fournit pas. */
export type PromotionLine = {
  serviceId: string
  name: string
  group: string | null
  original: Amounts
  discounted: Amounts
  savingLabel: string | null
}

/**
 * Les lignes du tableau d'une promotion, dans l'ordre où les prestations ont
 * été retenues. Les prestations introuvables au catalogue sont **ignorées
 * silencieusement** : une prestation supprimée du catalogue ne doit pas faire
 * tomber la page publique, et l'administration signale l'écart de son côté.
 */
export function promotionLines(
  // `appliesTo` facultatif : le site lit la charge utile de l'API sans la
  // repasser au schéma, et une copie de secours antérieure au champ ne le
  // porte pas — son absence vaut `tout`, comme dans le schéma.
  promotion: Pick<Promotion, 'serviceIds' | 'discountPercent' | 'rounding'> & Partial<Pick<Promotion, 'appliesTo'>>,
  services: Map<string, Pick<Service, 'name' | 'amounts'> & { label: string; group?: string }>,
): PromotionLine[] {
  const appliesTo = promotion.appliesTo ?? 'tout'
  return promotion.serviceIds.flatMap((serviceId) => {
    const service = services.get(serviceId)
    if (!service) return []
    const economie = savingCents(service.amounts, promotion.discountPercent, promotion.rounding, appliesTo)
    return [
      {
        serviceId,
        name: service.label,
        group: service.group ?? null,
        original: service.amounts,
        discounted: discountedAmounts(service.amounts, promotion.discountPercent, promotion.rounding, appliesTo),
        savingLabel: economie !== null && economie > 0 ? `−${formatCents(economie)} économisés` : null,
      },
    ]
  })
}

/**
 * Les lignes d'une promotion rangées par groupe du catalogue, les groupes dans
 * l'ordre de leur première prestation retenue et les lignes dans le leur. Ni
 * tri alphabétique ni réordonnancement : l'ordre reste celui de la gérante.
 */
export function groupPromotionLines(lines: PromotionLine[]): { group: string | null; lines: PromotionLine[] }[] {
  const groupes: { group: string | null; lines: PromotionLine[] }[] = []
  for (const line of lines) {
    const groupe = groupes.find((g) => g.group === line.group)
    if (groupe) groupe.lines.push(line)
    else groupes.push({ group: line.group, lines: [line] })
  }
  return groupes
}

/**
 * Situation tarifaire imposée aux prestations d'une promotion : celle de la
 * première retenue, ou `null` si la promotion est encore vide.
 *
 * La règle qu'elle sert : un tableau ne peut pas afficher côte à côte une
 * ligne « Avec pass / Sans pass » et une ligne « prix par séance / forfait ».
 * La situation (et non le seul `type`) est l'unité de comparaison, parce que
 * c'est le vocabulaire déjà présenté à la gérante dans l'éditeur du catalogue,
 * et parce que « séance partagée » et « cure » s'affichent différemment tout
 * en partageant le type `quantite`.
 */
export function promotionSituation(
  serviceIds: string[],
  amountsById: Map<string, Amounts>,
): Situation | null {
  for (const id of serviceIds) {
    const amounts = amountsById.get(id)
    if (amounts) return situationDe(amounts)
  }
  return null
}

// ─────────────────────────────── Jeux ───────────────────────────────

/**
 * L'étiquette publique d'un jeu : « Jeu estival · 1ᵉʳ juillet – 31 août 2026 ».
 * Composée de l'accroche et des dates, donc jamais à ressaisir quand la période
 * change. Sans accroche, seule la période est rendue.
 */
export function gamePeriodLabel(game: Pick<Game, 'tagline' | 'season'>): string {
  const periode = formatFrenchPeriod(game.season.start, game.season.end)
  const accroche = game.tagline.trim()
  return accroche ? `${accroche} · ${periode}` : periode
}
